import Footer from "components/sections/footer"
import Header from "components/sections/headerV2"
import Seo from "components/sections/seo"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import React from "react"
import { getCorporationSchema, getSchemaWithFallback } from "../../utils/schemaHelpers"
import Template from "./template"

const TemplateContainer = ({ data: { footer, header, template } }) => {
  const fallbackSchema = getCorporationSchema()
  
  // Use Contentful json if present, otherwise use fallback
  const schema = getSchemaWithFallback(template.meta?.json, fallbackSchema)

  return (
    <>
      <Seo
        {...template.meta}
        id={template.id}
        schemaMarkup={schema}
        slug={template.slug}
      />
      <Header {...header} />
      <Template {...template} />
      <Footer {...footer} />
    </>
  )
}

TemplateContainer.props = {
  ...Template.props,
  meta: PropTypes.shape(Seo.props),
}

TemplateContainer.propTypes = {
  data: PropTypes.shape({
    footer: PropTypes.shape(Footer.props),
    header: PropTypes.shape(Header.props),
    template: PropTypes.shape(TemplateContainer.props),
  }),
}

export default TemplateContainer

export const query = graphql`
  fragment ButtonV2Fields on ContentfulComponentButtonV2 {
    id
    accessibilityLabel
    showArrow
    buttonSize
    buttonStyle
    path
    text
  }
  fragment ImageFields on ContentfulAsset {
    title
    description
    file {
      url
    }
  }

  query WebPage($slug: String) {
    header: contentfulGlobalHeader(name: { eq: "Header" }) {
      announcement {
        json
      }
      menu {
        menus {
          label
          links {
            text
            path
            nestedLinks {
              text
              path
            }
          }
        }
      }
      utilityLinks {
        path
        text
      }
      cta {
        href: path
        text
      }
      callsToAction {
        ...ButtonV2Fields
      }
    }
    footer: contentfulGlobalFooter(name: { eq: "Footer" }) {
      menu {
        menus {
          label
          links {
            path
            text
          }
        }
      }
      scheduleAppointment {
        path
        text
      }
      socialLinks {
        path
        text
      }
      utilityLinks {
        path
        text
      }
    }
    template: contentfulWebPage(slug: { eq: $slug }) {
      slug
      title
      sections {
        __typename
        ... on ContentfulSectionHomeHeroV2 {
          id
          heroTitle
          homeText
          homeHeroCallsToActions {
            ...ButtonV2Fields
          }
          homeHeroImage {
            ...ImageFields
            fluid(maxWidth: 568, quality: 100) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          homeHeroCards {
            id
            homeHeroCardTitle
            homeHeroCardTheme
            homeHeroCardImage {
              ...ImageFields
            }
          }
        }
        ... on ContentfulSectionLogoBarV2 {
          id
          heading
          logos {
            ...ImageFields
            fluid(maxWidth: 220, quality: 100) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          ctaButton {
            ...ButtonV2Fields
          }
        }
        ... on ContentfulSectionHomeSplitStickyV2 {
          id
          headingTitle: heading {
            heading
          }
          ctaButton {
            ...ButtonV2Fields
          }
          cards {
            id
            title
            text {
              text
            }
            url
            image {
              ...ImageFields
              fluid(maxWidth: 962, quality: 90) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
        }
        ... on ContentfulSectionSearchAndFeaturedClinicsV2 {
          id
          heading
          text {
            text
          }
          featuredClinics {
            id
            name
            detailedAddress
            slug
            image {
              ...ImageFields
              fluid(maxWidth: 992, quality: 90) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
          ctaButton {
            ...ButtonV2Fields
          }
        }
        ... on ContentfulSectionSplitSectionV2 {
          id
          callsToAction {
            ...ButtonV2Fields
          }
          subTitle
          title
          heading
          contentOrientation
          image {
            ...ImageFields
            fluid(maxWidth: 686, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          content {
            json
          }
        }
        ... on ContentfulSectionTestimonials {
          id
          name
          intro {
            title {
              id
              text {
                json
              }
            }
            text {
              json
            }
          }
          cards {
            id
            name
            image {
              image {
                ...ImageFields
                fluid(maxWidth: 464, quality: 90) {
                  ...GatsbyContentfulFluid_withWebp
                }
              }
            }
            quote {
              quote
              childMarkdownRemark {
                html
              }
            }
            quoteAttribution
            statistics {
              id
              name
              percent
              copy
            }
          }
          callToActionButton {
            ...ButtonV2Fields
          }
        }
        ... on ContentfulSectionPageHeroV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
          pageHeroImage {
            ...ImageFields
            fluid(maxWidth: 516, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          pageHeroIcons {
            id
            position
            title
            icon {
              ...ImageFields
            }
          }
          pageHeroCallsToActions {
            ...ButtonV2Fields
          }
          contentOrientation
        }
        ... on ContentfulSectionContactJumpstartCtaV2 {
          jumpstartCtaHeading: heading {
            heading
          }
          text {
            childrenMarkdownRemark {
              html
            }
          }
          contactCtaText {
            json
          }
          phoneNumber
          ctaButton {
            ...ButtonV2Fields
          }
          photo {
            ...ImageFields
            fluid(maxWidth: 1312, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
        }
        ... on ContentfulSectionVersusV2 {
          id
          title
          vsHeading: heading {
            heading
          }
          versusColumnSections {
            id
            title
            versusSubSections {
              id
              title
              text {
                childrenMarkdownRemark {
                  html
                }
              }
            }
          }
          callsToAction {
            ...ButtonV2Fields
          }
        }
        ... on ContentfulSectionSplitStepsUpV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
          steps {
            id
            title
            text
            icon {
              ...ImageFields
            }
          }
        }
        ... on ContentfulSectionSplitAccordionV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
          accordionItems {
            heading
            id
            title
            icon {
              ...ImageFields
            }
            subHeading
            content {
              childrenMarkdownRemark {
                html
              }
            }
          }
        }
        ... on ContentfulSectionRoadmapV2 {
          id
          title
          roadmapHeading: heading {
            heading
          }
          callsToAction {
            ...ButtonV2Fields
          }
          roadmapSteps: roadmapStep {
            id
            title
            image {
              ...ImageFields
              fluid(maxWidth: 412, quality: 90) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
            icon {
              ...ImageFields
            }
            text {
              childrenMarkdownRemark {
                html
              }
            }
          }
        }
        ... on ContentfulSectionSplitImageCarouselV2 {
          id
          title
          subHeading: subTitle {
            subTitle
          }
          text {
            childrenMarkdownRemark {
              html
            }
          }
          callsToAction {
            ...ButtonV2Fields
          }
          slides {
            title
            id
            heading
            subHeading
            image {
              ...ImageFields
              fluid(maxWidth: 720, quality: 100) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
        }
        ... on ContentfulSectionCardGridV2 {
          id
          title
          text {
            text
          }
          callsToAction {
            ...ButtonV2Fields
          }
          gridItems {
            id
            linkUrl
            title
            text {
              text
            }
          }
        }
        ... on ContentfulSectionPathV2 {
          id
          heading
          pathText: text
          titleCardTitle
          titleCardText
          titleCardLinksTitle
          titleCardHeading
          colorTheme
          callToActionButton {
            ...ButtonV2Fields
          }
          pathCards {
            id
            title
            icon {
              ...ImageFields
            }
            ctaButton {
              ...ButtonV2Fields
            }
            text {
              text
              childMarkdownRemark {
                html
              }
            }
          }
          titleCardLinks {
            id
            linkText
            linkUrl {
              linkUrl
            }
          }
        }
        ... on ContentfulSectionAccordionV2 {
          id
          heading
          accordionItems {
            id
            accordionHeading: heading
            content {
              childrenMarkdownRemark {
                html
              }
            }
          }
          cta: callsToAction {
            ...ButtonV2Fields
          }
        }
        ... on ContentfulSectionSplitContentListV2 {
          id
          subSections {
            id
            title
            anchor
            subTitle
            text {
              childrenMarkdownRemark {
                html
              }
            }
            icon {
              ...ImageFields
            }
            image {
              ...ImageFields
              fluid(maxWidth: 1025, quality: 100) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
          callsToAction {
            ...ButtonV2Fields
          }
        }
        ... on ContentfulSectionLinkGridV2 {
          id
          title
          linkSubTitle: subTitle
          text {
            childrenMarkdownRemark {
              html
            }
          }
          links {
            id
            title
            link
          }
        }
        ... on ContentfulSectionTheWholeChildModelV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
        }
        ... on ContentfulSectionDarkmodeSplitContentV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
          ctaButton {
            ...ButtonV2Fields
          }
          image {
            ...ImageFields
            fluid(maxWidth: 530, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          mobileImage {
            ...ImageFields
            fluid(maxWidth: 530, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          mobileText {
            childrenMarkdownRemark {
              html
            }
          }
        }
        ... on ContentfulSectionHorizontalCardSlideV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
          cards {
            id
            title
            subTitle
            text {
              childrenMarkdownRemark {
                html
              }
            }
          }
        }
        ... on ContentfulSectionGridAccordionV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
          accordions {
            heading
            id
            title
            icon {
              ...ImageFields
            }
            subHeading
            content {
              childrenMarkdownRemark {
                html
              }
            }
          }
        }
        ... on ContentfulSectionComparisonTableV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
          row {
            id
            title
            cortica
            standard
          }
          callsToAction {
            ...ButtonV2Fields
          }
        }
        ... on ContentfulSectionDarkmodePodcastHighlightV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
          image {
            ...ImageFields
            fluid(maxWidth: 323, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          spotifyLink
          applePodcastLink
          youtubeLink
        }
        ... on ContentfulSectionBookHighlightV2 {
          id
          title
          text {
            childrenMarkdownRemark {
              html
            }
          }
          bookImage {
            ...ImageFields
            fluid(maxWidth: 505, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          orderLink
          audibleLink
          quote {
            childrenMarkdownRemark {
              html
            }
          }
          cite
          quotePhoto {
            ...ImageFields
            fluid(maxWidth: 467, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
        }
        ... on ContentfulSectionBlogFeature {
          __typename
          title
          careNote {
            createdAt
            id
            slug
            title
            description {
              description
            }
            icon {
              file {
                url
              }
              title
              description
            }
            category {
              id
              name
              theme
            }
          }
          cards {
            action: action {
              action {
                ... on ContentfulComponentButton {
                  accessibilityLabel
                  color
                  isOutlined
                  isSmall
                  href: path
                  text
                }
              }
            }
            title
            description {
              description
            }
            icon {
              file {
                url
              }
              title
              description
            }
          }
        }
        ... on ContentfulSectionFeaturedArticles {
          __typename
          title
          featuredArticles {
            createdAt
            id
            slug
            title
            description {
              description
            }
            icon {
              file {
                url
              }
              title
              description
            }
            category {
              id
              name
              theme
            }
          }
        }
        ... on ContentfulSectionSplitContent {
          __typename
          backgroundColor
          reverse
          image {
            image {
              alt: description
              title
              file {
                url
              }
            }
          }
          intro {
            eyebrow: subTitle
            action: callToAction {
              accessibilityLabel
              color
              isOutlined
              isSmall
              href: path
              text
            }
            text {
              json
            }
            title {
              text {
                json
              }
            }
          }
        }
        ... on ContentfulSectionFaq {
          __typename
          action: callToAction {
            accessibilityLabel
            color
            isOutlined
            isSmall
            href: path
            text
          }
          intro {
            eyebrow: subTitle
            action: callToAction {
              accessibilityLabel
              color
              isOutlined
              isSmall
              href: path
              text
            }
            text {
              json
            }
            title {
              text {
                json
              }
            }
          }
          questions {
            title: question
            text: answer {
              json
            }
          }
        }
        ... on ContentfulOrderedList {
          __typename
          title
          type
          headlineOrdered {
            headlineOrdered
          }
          items {
            __typename
            ... on ContentfulStaffMember {
              __typename
              department
              id
              name
              role
              slug
              picture {
                title
                fluid {
                  src
                }
              }
            }
            ... on ContentfulTreatment {
              __typename
              cta
              name
              slug
              style
              description {
                description
              }
              icon {
                title
                file {
                  url
                }
              }
            }
          }
        }
        ... on ContentfulSectionQuote {
          __typename
          name
          quote {
            id
            quote
          }
        }
        ... on ContentfulSectionRichText {
          __typename
          alignment
          details {
            json
          }
          web_page {
            title
          }
        }
        ... on ContentfulSectionListOfActions {
          __typename
          id
          actions {
            buttonText
            id
            style
            action {
              __typename
              ... on ContentfulActionRedirect {
                __typename
                url
              }
            }
          }
        }
        ... on ContentfulSubheader {
          __typename
          backgroundColor
          headline
          description {
            description
          }
        }
        ... on ContentfulSectionCards {
          __typename
          headline
          subheadline
          actions {
            buttonText
            style
            action {
              __typename
              ... on ContentfulActionRedirect {
                __typename
                url
                openInANewTab
              }
            }
          }
          cards {
            action {
              action {
                __typename
                ... on ContentfulActionRedirect {
                  __typename
                  url
                  openInANewTab
                }
              }
            }
            description {
              description
            }
          }
        }
        ... on ContentfulSectionSideBySide {
          __typename
          subtitle
          title
          sections {
            __typename
            ... on ContentfulSectionDynamic {
              __typename
              sections
              slug
              style
              title
            }
            ... on ContentfulParagraphsList {
              __typename
              layout
              title
              paragraphs {
                alignment
                title
                description {
                  description
                }
                icon {
                  title
                  file {
                    url
                  }
                }
              }
              actions {
                buttonText
                style
                action {
                  __typename
                  ... on ContentfulActionRedirect {
                    __typename
                    openInANewTab
                    title
                    url
                  }
                }
              }
            }
          }
        }
        ... on ContentfulSectionDynamic {
          __typename
          dynamicSection: sections
          backgroundColor
          style
          title
          additionalSettings {
            enableDirectionsCTA
            testimonialsCount
          }
          additionalText {
            json
          }
        }
        ... on ContentfulSectionHeroVideo {
          __typename
          backgroundColor
          style
          subtitle
          title
          actions {
            buttonText
            style
            buttonImage {
              title
              file {
                url
              }
            }
            action {
              __typename
              ... on ContentfulActionRedirect {
                __typename
                title
                url
              }
            }
          }
          backgroundImage {
            title
            file {
              url
            }
            fluid(maxWidth: 1000, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          backgroundVideo {
            title
            file {
              url
            }
          }
          description {
            description
          }
          photo {
            file {
              url
            }
          }
          navigator {
            buttonText
            style
            action {
              __typename
              ... on ContentfulActionNavigate {
                __typename
                section {
                  __typename
                  ... on ContentfulSectionText {
                    __typename
                    title
                  }
                }
              }
            }
            buttonImage {
              title
              fluid(maxWidth: 200, quality: 90) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
          video {
            title
            url
          }
          web_page {
            slug
            title
          }
        }
        ... on ContentfulSectionText {
          __typename
          headline
          style
          title
          body {
            body
            childMarkdownRemark {
              html
            }
          }
          textSectionActions {
            buttonText
            name
            style
            action {
              __typename
              ... on ContentfulActionRedirect {
                __typename
                url
              }
            }
          }
        }
        ... on ContentfulSectionWrapper {
          __typename
          backgroundStyle
          title
          backgroundImage {
            title
            fluid(maxWidth: 1000, quality: 90) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          wrapperSections: sections {
            __typename
            ... on ContentfulSectionQuote {
              __typename
              name
              quote {
                id
                quote
              }
            }
            ... on ContentfulCallToAction {
              __typename
              buttonText
              id
              name
              style
              action {
                __typename
                ... on ContentfulActionRedirect {
                  __typename
                  id
                  openInANewTab
                  url
                }
              }
              buttonImage {
                title
                file {
                  url
                }
              }
            }
            ... on ContentfulSectionListOfAccordionCards {
              __typename
              id
              style
              cards {
                headline
                id
                subheadline
                content {
                  content
                  childMarkdownRemark {
                    html
                  }
                }
              }
            }
            ... on ContentfulSectionQuote {
              __typename
              id
              name
              quote {
                quote
              }
            }
            ... on ContentfulHeadline {
              __typename
              id
              headline
            }
            ... on ContentfulSectionListOfActions {
              __typename
              id
              actions {
                buttonText
                style
                action {
                  __typename
                  ... on ContentfulActionRedirect {
                    __typename
                    openInANewTab
                    url
                  }
                }
              }
            }
            ... on ContentfulSectionWrapper {
              __typename
              backgroundStyle
              title
              wrapperSections: sections {
                __typename
                ... on ContentfulSectionSideBySide {
                  __typename
                  name
                  style
                  customClass
                  sections {
                    __typename
                    ... on ContentfulSectionForm {
                      __typename
                      formTitleColor
                      subject
                      title
                      description {
                        childMarkdownRemark {
                          html
                        }
                      }
                      disclaimer {
                        childMarkdownRemark {
                          html
                        }
                      }
                      inputFields {
                        __typename
                        ... on ContentfulFormInputFieldSelect {
                          __typename
                          id
                          label
                          options
                          required
                          source
                        }
                        ... on ContentfulFormInputFieldText {
                          __typename
                          label
                          maximumCount
                          placeholder
                          required
                          type
                        }
                        ... on ContentfulFormInputFieldSideBySide {
                          __typename
                          label
                          inputFields {
                            __typename
                            ... on ContentfulFormInputFieldText {
                              __typename
                              id
                              label
                              maximumCount
                              placeholder
                              required
                              type
                            }
                          }
                        }
                        ... on ContentfulFormInputFieldHidden {
                          __typename
                          class
                          dataFieldName
                          fieldId
                          name
                          title
                        }
                      }
                      submitAction {
                        buttonText
                        style
                        action {
                          __typename
                          ... on ContentfulActionFormSubmission {
                            __typename
                            action
                            title
                          }
                        }
                      }
                    }
                    ... on ContentfulSectionRichText {
                      __typename
                      alignment
                      details {
                        json
                      }
                    }
                  }
                }
                ... on ContentfulSectionRichText {
                  __typename
                  alignment
                  details {
                    json
                  }
                }
              }
            }
            ... on ContentfulSectionSideBySide {
              __typename
              name
              style
              customClass
              sections {
                __typename
                ... on ContentfulSectionForm {
                  __typename
                  formTitleColor
                  subject
                  title
                  description {
                    content: description
                  }
                  disclaimer {
                    childMarkdownRemark {
                      html
                    }
                  }
                  inputFields {
                    __typename
                    ... on ContentfulFormInputFieldSelect {
                      __typename
                      id
                      label
                      options
                      required
                      source
                    }
                    ... on ContentfulFormInputFieldText {
                      __typename
                      label
                      maximumCount
                      placeholder
                      required
                      type
                    }
                    ... on ContentfulFormInputFieldSideBySide {
                      __typename
                      label
                      inputFields {
                        __typename
                        ... on ContentfulFormInputFieldText {
                          __typename
                          id
                          label
                          maximumCount
                          placeholder
                          required
                          type
                        }
                      }
                    }
                    ... on ContentfulFormInputFieldHidden {
                      __typename
                      class
                      dataFieldName
                      fieldId
                      name
                      title
                    }
                  }
                  submitAction {
                    buttonText
                    style
                    action {
                      __typename
                      ... on ContentfulActionFormSubmission {
                        __typename
                        action
                        title
                      }
                    }
                  }
                }
                ... on ContentfulSectionRichText {
                  __typename
                  alignment
                  details {
                    json
                  }
                }
              }
            }
            ... on ContentfulSectionRichText {
              __typename
              alignment
              details {
                json
              }
            }
            ... on ContentfulSectionText {
              __typename
              headline
              style
              title
              body {
                body
                childMarkdownRemark {
                  html
                }
              }
            }
            ... on ContentfulVideoWrapper {
              __typename
              title
              url
            }
            ... on ContentfulParagraphsList {
              __typename
              layout
              title
              paragraphs {
                alignment
                title
                description {
                  description
                }
                icon {
                  title
                  file {
                    url
                  }
                }
              }
              actions {
                buttonText
                style
                action {
                  __typename
                  ... on ContentfulActionRedirect {
                    __typename
                    openInANewTab
                    title
                    url
                  }
                }
              }
            }
          }
        }
      }
      meta: seoMetadata {
        canonical
        keywords
        title
        type
        json
        abstract {
          abstract
        }
        description {
          description
        }
        image {
          file {
            url
          }
        }
      }
    }
  }
`
